/**
 * BriefsView Component
 *
 * Main view for managing content briefs.
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DocumentIcon } from "@/components/icons";
import { EntityCreateButton, ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import {
  useBriefsQuery,
  useCreateBriefMutation,
  useDeleteBriefMutation,
  useShareBriefMutation,
  useUpdateBriefMutation,
} from "./hooks/useBriefs";
import {
  BriefListItem,
  BriefStatus,
  CreateBriefRequest,
} from "./types";
import { BriefCard } from "./components/BriefCard";
import { CreateBriefModal } from "./components/CreateBriefModal";
import { BRIEF_STATUS_CONFIG } from "./briefStatusConfig";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";

const STATUS_TABS: { id: BriefStatus | "ALL"; label: string; dotColor?: string }[] = [
  { id: "ALL", label: "Tutti" },
  { id: "DRAFT", label: BRIEF_STATUS_CONFIG.DRAFT.label, dotColor: BRIEF_STATUS_CONFIG.DRAFT.dot },
  { id: "CAPTURING", label: BRIEF_STATUS_CONFIG.CAPTURING.label, dotColor: BRIEF_STATUS_CONFIG.CAPTURING.dot },
  { id: "EDITING", label: BRIEF_STATUS_CONFIG.EDITING.label, dotColor: BRIEF_STATUS_CONFIG.EDITING.dot },
  { id: "COMPLETE", label: BRIEF_STATUS_CONFIG.COMPLETE.label, dotColor: BRIEF_STATUS_CONFIG.COMPLETE.dot },
];

export const BriefsView: React.FC = () => {
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.brief.plural);

  // Tab filter lives in the URL (?status=) so origin-aware back navigation from
  // a brief detail restores it. DRAFT is the default → no param.
  const activeTab = (searchParams.get("status") as BriefStatus | "ALL") || "DRAFT";
  const setActiveTab = (tab: BriefStatus | "ALL") => {
    setSearchParams(tab === "DRAFT" ? {} : { status: tab });
  };
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    brief: BriefListItem | null;
  }>({ isOpen: false, brief: null });

  const { data: briefs = [], isLoading, error } = useBriefsQuery(clientId);

  const filteredBriefs =
    activeTab === "ALL"
      ? briefs
      : briefs.filter((b) => b.status === activeTab);

  const createMutation = useCreateBriefMutation(clientId || "");
  const deleteMutation = useDeleteBriefMutation(clientId || "");
  const shareMutation = useShareBriefMutation(clientId || "");
  const updateMutation = useUpdateBriefMutation(clientId || "");

  const handleCreate = async (data: CreateBriefRequest) => {
    const brief = await createMutation.mutateAsync(data);
    setIsCreateModalOpen(false);
    navigate(`../briefs/${brief.id}`, trailNavOptions);
  };

  const handleBriefClick = (brief: BriefListItem) => {
    navigate(`../briefs/${brief.id}`, trailNavOptions);
  };

  const handleDelete = (brief: BriefListItem) => {
    setDeleteConfirm({ isOpen: true, brief });
  };

  const handleShare = async (brief: BriefListItem) => {
    return shareMutation.mutateAsync(brief.id);
  };

  const handleStatusChange = async (
    brief: BriefListItem,
    status: BriefStatus
  ) => {
    await updateMutation.mutateAsync({ briefId: brief.id, data: { status } });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.brief) {
      await deleteMutation.mutateAsync(deleteConfirm.brief.id);
    }
    setDeleteConfirm({ isOpen: false, brief: null });
  };

  const tabItems: TabItem[] = STATUS_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.dotColor ? <div className={`w-2 h-2 rounded-full ${tab.dotColor}`} /> : undefined,
  }));

  const tabConfig: TabGroupConfig = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    fullWidth: false,
    hasBackground: true,
  };

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Client Selected
          </h2>
          <p className="text-gray-500">
            Please select a client to manage their briefs
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={tabItems}
            activeId={activeTab}
            onTabChange={(id) => setActiveTab(id as BriefStatus | "ALL")}
            config={tabConfig}
          />
          <PageHeader.Actions>
            <EntityCreateButton
              entity="brief"
              onClick={() => setIsCreateModalOpen(true)}
            />
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <PageSection.Canvas>
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-gray-500">Loading briefs...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-red-500">
              Error loading briefs:{" "}
              {error instanceof Error ? error.message : "Unknown error"}
            </div>
          </div>
        ) : filteredBriefs.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1">
            <div className="text-gray-400 mb-4">
              <DocumentIcon className="w-16 h-16" strokeWidth={1} />
            </div>
            <p className="text-gray-500 mb-1">
              {activeTab === "ALL"
                ? "No briefs yet"
                : `No ${activeTab.toLowerCase()} briefs`}
            </p>
            <p className="text-gray-400 text-sm mb-4 max-w-sm text-center">
              Briefs bundle the ideas for a shoot and send them to your
              photographer.
            </p>
          </div>
        ) : (
          <div
            className="grid gap-4 py-1"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(200px, calc((100% - 2rem) / 3), 320px), 1fr))' }}
          >
            {filteredBriefs.map((brief) => (
              <BriefCard
                key={brief.id}
                brief={brief}
                onView={handleBriefClick}
                onShare={handleShare}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
                isUpdatingStatus={updateMutation.isPending}
              />
            ))}
          </div>
        )}
      </PageSection.Canvas>

      {/* Create Modal */}
      <CreateBriefModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        brief={null}
        isLoading={createMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, brief: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Brief"
        message={`Are you sure you want to delete "${deleteConfirm.brief?.title || "this brief"}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </PageLayout>
  );
};
